import { useAppSelector } from "../state/hooks";
import { FullContestEntryDetails, getFullContestEntryDetailsById } from "../state/slices/contests-slice";
import { Bet } from "interfaces/bet";
import { getGroupState } from "state/slices/group-slice.ts";

interface ContestantPickAllowedCheckResult {
  isLoading: boolean;
  isPickAllowed: boolean;
}

/**
 * Determines if a specific contestant pick is allowed (not locked)
 * within the currently active contest entry. A pick is disallowed if
 * it has already been picked in the contest entry.
 * Also provides the loading state of the underlying contest entry bet data.
 */
const useContestContestantPickAllowedCheck = (contestantName: string, contestEntryId: string | undefined): ContestantPickAllowedCheckResult => {
  const group = useAppSelector(getGroupState);

  const isContestGroup = group.contest_group;

  const fullContestEntryDetails: FullContestEntryDetails | undefined =
    useAppSelector(state => getFullContestEntryDetailsById(state, contestEntryId));

  if (!isContestGroup || !contestEntryId) {
    return { isLoading: false, isPickAllowed: true };
  }

  if (!fullContestEntryDetails) {
    return { isLoading: true, isPickAllowed: true };
  }

  const isLoading = fullContestEntryDetails.loadingState === "loading" || fullContestEntryDetails.loadingState === "idle";

  let isPickAllowed: boolean = true;

  // Evaluate against whatever bets we currently hold. During the refresh that
  // follows a pick the state is "loading", but the previously-fetched bets
  // (including the just-placed optimistic pick) are retained — so we must keep
  // honoring them. Only computing this while "succeeded" briefly reopened the
  // contestant mid-refresh and let a duplicate pick slip through.
  const hasReliableBets =
    fullContestEntryDetails.loadingState === "succeeded" ||
    fullContestEntryDetails.loadingState === "loading";

  if (contestEntryId && hasReliableBets) {
    const hasAlreadyPickedTheContestant = fullContestEntryDetails.bets.some(
      (bet: Bet) => bet.contestant_name === contestantName
    );
    isPickAllowed = !hasAlreadyPickedTheContestant;
  }

  return {
    isLoading,
    isPickAllowed,
  };
};

export default useContestContestantPickAllowedCheck; 